import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { isArray, isNil } from 'lodash';

// Components
import FlashMessage from '../../molecules/FlashMessage/FlashMessage';

import styles from './SelectField.module.scss';
import CustomSelect from './CustomSelect/CustomSelect';
import cn from '../../../utils/tools/cn';

function SelectField({
  id,
  name,
  options,
  label,
  className,
  selectClassName,
  error,
  setFieldTouched,
  setFieldValue,
  value,
  isMulti,
  withTags,
  onChange,
  isRequired,
  disabled,
  clearable,
  darkBg,
  menuPosition,
  maxNumberOfItems,
}) {
  const { t } = useTranslation();
  const [focus, setFocus] = useState(false);

  const handleBlur = () => {
    setFocus(false);
    setFieldTouched(name, true);
  };

  const hasValue = () => {
    if (isArray(value)) return value?.length > 0;
    return value;
  };

  const handleChange = (input) => {
    if (!maxNumberOfItems || (input.length <= maxNumberOfItems)) {
      onChange(input);
      setFieldValue(name, input);
    }
  };

  useEffect(() => {
    if (isNil(value)) setFocus(false);
  }, [value]);

  return (
    <div className={cn(['w-100', className || ''])}>
      {hasValue() && isMulti && isArray(value) && withTags ? (
        <div className="m-b-10">
          {value.map((val, i) => (
            <span className={styles.value_tag} key={i}>{val.label}</span>
          ))}
        </div>
      ) : null}

      <div
        className={cn([
          'd-flex a-stretch',
          styles.select_wrapper,
          selectClassName || '',
          error ? styles.error : '',
          hasValue() || focus ? styles.has_value : '',
          disabled ? styles.disabled : '',
          darkBg ? 'select_dark-bg' : '',
        ])}
      >
        {label ? (
          <label htmlFor={id}>
            {label}
            {isRequired ? <span>*</span> : ''}
          </label>
        ) : null}

        {typeof window !== 'undefined' ? (
          <CustomSelect
            id={id}
            name={name}
            options={options}
            onChange={handleChange}
            onBlur={(input) => handleBlur(input)}
            value={value}
            onFocus={() => setFocus(true)}
            isMulti={isMulti}
            closeOnSelect={!isMulti}
            disabled={disabled}
            clearable={clearable}
            noLabel={isNil(label)}
            menuPosition={menuPosition}
            allowSelectAll={!maxNumberOfItems}
          />
        ) : null}
      </div>

      {maxNumberOfItems ? (
        <p className="m-t-8 m-l-5 fs-12 c-neutral-03">{t('common.maximum', { num: maxNumberOfItems })}</p>
      ) : null}

      {error ? (
        <div id={`error_${name}`} className="m-t-10">
          <FlashMessage message={t(error)} color="error" />
        </div>
      ) : null}
    </div>
  );
}

SelectField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  label: PropTypes.string,
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  error: PropTypes.string,
  setFieldTouched: PropTypes.func,
  setFieldValue: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
  isMulti: PropTypes.bool,
  withTags: PropTypes.bool,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  darkBg: PropTypes.bool,
  menuPosition: PropTypes.oneOf(['fixed']),
  maxNumberOfItems: PropTypes.number,
};

SelectField.defaultProps = {
  options: [],
  label: null,
  className: null,
  selectClassName: null,
  error: null,
  setFieldTouched: () => {},
  setFieldValue: () => {},
  onChange: () => {},
  value: null,
  isMulti: false,
  withTags: false,
  isRequired: false,
  disabled: false,
  clearable: true,
  darkBg: false,
  menuPosition: null,
  maxNumberOfItems: null,
};

export default SelectField;
